import { $, $$, setLoading } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { formToObject, appendFiles, clearFileInputs } from '../lib/form.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { toastError, toastSuccess } from '../lib/toast.js';
import {
  CARD_BACKGROUND_WARNINGS,
  mountCardPreview,
  readAppearance,
} from '../lib/card-preview.js';

export function initWelcomePage(): void {
  const form = $<HTMLFormElement>('[data-welcome-form]');
  if (!form) return;

  const guildId = form.dataset.guildId ?? '';

  /*
   * Saving is armed before the widgets: saving is the essential function of the
   * page, it must not depend on a picker mounting nor on reading the data
   * block, both of which can fail.
   */
  trackDirtyForm({
    form,
    successMessage: ct('configSaved', 'Configuration saved.'),
    onSave: async () => {
      const values = formToObject(form);
      values.guildId = guildId;

      /*
       * The ordered block list is not a form field.
       *
       * It is a list somebody reorders with arrows, and `formToObject` reads
       * inputs. Read at save time rather than kept in a variable: the save has
       * to serialise what is on screen at that instant.
       */
      values.landing = { ...(values.landing as object), blocks: landingBlocks(form) };
      /*
       * The images travel as multipart, the rest as JSON in the `payload`
       * field: this avoids flattening a nested structure into dozens of form
       * fields, which is what the old code did - at the cost of an unguarded
       * `JSON.parse` on the server.
       */
      const body = new FormData();
      body.set('payload', JSON.stringify(values));
      appendFiles(form, body);

      const answer = await api.put<{ warnings?: string[] }>(
        `/api/welcome?guildId=${guildId}`,
        body,
      );

      // The files are now stored on the server: we clear the fields so they are
      // not re-uploaded on the next save.
      clearFileInputs(form);

      /*
       * Reported after the save, never instead of it.
       *
       * A block switched on without a channel is worth saying — it will do
       * nothing — but it is not a reason to refuse the whole page. Refusing is
       * what this page used to do, and it made an unrelated compartment
       * impossible to edit.
       */
      for (const warning of answer.warnings ?? []) {
        toastError(ct(warning, WARNING_FALLBACKS[warning] ?? warning));
      }
    },
  });

  mountLanding(form, guildId);

  // The optional DM text only matters when the box is ticked; showing it either
  // way would put a field nobody asked for between the message and its embed.
  for (const box of $$<HTMLInputElement>('[data-toggle-dm]', form)) {
    const field = $('[data-dm-message]', form);

    box.addEventListener('change', () => {
      field?.classList.toggle('hidden', !box.checked);
    });
  }

  // ─── Sections repliables ─────────────────────────────────────────────────
  for (const toggle of $$<HTMLInputElement>('[data-toggle-section]', form)) {
    const section = toggle.closest('section');
    const body = section ? $('[data-section-body]', section) : null;

    toggle.addEventListener('change', () => {
      body?.classList.toggle('hidden', !toggle.checked);
    });
  }

  // ─── Bascule message simple / embed ──────────────────────────────────────
  for (const toggle of $$<HTMLInputElement>('[data-toggle-embed]', form)) {
    const block = toggle.closest('[data-block]');
    if (!block) continue;

    const plain = $('[data-plain-message]', block);
    const embed = $('[data-embed-config]', block);

    toggle.addEventListener('change', () => {
      plain?.classList.toggle('hidden', toggle.checked);
      embed?.classList.toggle('hidden', !toggle.checked);
    });
  }

  /*
   * Generated card.
   *
   * The field keeps its `name`, so `formToObject` serialises it in every case:
   * hiding the section must not erase what is written there, only stop showing
   * it.
   */
  for (const toggle of $$<HTMLInputElement>('[data-card-toggle]', form)) {
    const key = toggle.dataset.cardToggle ?? '';
    const config = $(`[data-card-config="${key}"]`, form);

    toggle.addEventListener('change', () => {
      config?.classList.toggle('hidden', !toggle.checked);
    });

    /*
     * One preview per block, reading that block's own appearance.
     *
     * This is what makes the separation visible: tuning the welcome must not
     * redraw the goodbye card, and the other way round.
     */
    mountCardPreview({
      form,
      name: key,
      endpoint: `/api/welcome/card-preview?guildId=${guildId}`,
      read: () => ({
        guildId,
        card: readAppearance(form, key),
        title: fieldValue(form, `${key}.card.title`),
        subtitle: fieldValue(form, `${key}.card.subtitle`),
        message: fieldValue(form, `${key}.card.message`),
      }),
    });
  }

  // ─── Automatic roles ─────────────────────────────────────────────────────

  // ─── Preview ─────────────────────────────────────────────────────────────
  for (const button of $$<HTMLButtonElement>('[data-preview]', form)) {
    button.addEventListener('click', () => {
      void (async () => {
        const kind = button.dataset.preview;
        setLoading(button, true);
        try {
          const result = await api.post<{ message: string }>(
            `/api/welcome/preview?guildId=${guildId}`,
            { guildId, kind },
          );
          toastSuccess(result.message);
        } catch (error) {
          toastError(error);
        } finally {
          setLoading(button, false);
        }
      })();
    });
  }

  // ─── Sauvegarde ──────────────────────────────────────────────────────────
}

/** Reads one field by its exact name, whatever kind of control it is. */
function fieldValue(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
    ? field.value
    : '';
}

/* ------------------------------------------------------ salon d'accueil */

/** The block names, in the order they appear on screen. */
function landingBlocks(form: HTMLFormElement): string[] {
  return $$<HTMLElement>('[data-landing-list] [data-block-name]', form).map(
    (row) => row.dataset.blockName ?? '',
  );
}

/**
 * The ordered list of blocks, and the publish button.
 *
 * Publishing escapes the save bar on purpose: it posts or edits up to ten
 * messages in a channel. Behind "Save" it would fire on every unrelated change
 * to the page.
 */
function mountLanding(form: HTMLFormElement, guildId: string): void {
  const list = $<HTMLElement>('[data-landing-list]', form);
  const picker = $<HTMLSelectElement>('[data-landing-pick]', form);
  const add = $<HTMLButtonElement>('[data-landing-add]', form);
  const publish = $<HTMLButtonElement>('[data-landing-publish]', form);
  if (!list) return;

  /*
   * `notify` is false on the first render.
   *
   * Drawing the list fired a `change` on the form, so the page opened dirty and
   * the save bar was already up. "Cancel" reloads the page — which drew the
   * list again, which raised the bar again: the bar came back the instant it
   * was dismissed, for ever.
   */
  const draw = (names: string[], notify = true) => {
    list.replaceChildren();

    names.forEach((name, index) => {
      const row = document.createElement('li');
      row.className = 'flex flex-wrap items-center gap-2';
      row.dataset.blockName = name;

      const label = document.createElement('span');
      label.className = 'flex-1 text-sm';
      label.textContent = `${String(index + 1)}. ${name}`;

      row.append(label, arrow('up', index > 0), arrow('down', index < names.length - 1), remove());
      list.append(row);
    });

    if (notify) form.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const arrow = (way: 'up' | 'down', usable: boolean) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-ghost btn-xs';
    button.dataset.move = way;
    button.disabled = !usable;
    button.textContent = way === 'up' ? '↑' : '↓';
    return button;
  };

  const remove = () => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-ghost btn-xs text-error';
    button.dataset.drop = '';
    button.textContent = '✕';
    return button;
  };

  const initial = $<HTMLScriptElement>('#page-data');
  const data = initial ? (JSON.parse(initial.textContent ?? '{}') as PageData) : null;
  draw(data?.settings.landing.blocks ?? [], false);

  add?.addEventListener('click', () => {
    const name = picker?.value ?? '';
    // Ten is Discord's practical ceiling for a page somebody scrolls, not a
    // product preference: past it, it stops being a landing page.
    if (name && landingBlocks(form).length < 10) draw([...landingBlocks(form), name]);
  });

  list.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const row = target.closest<HTMLElement>('[data-block-name]');
    if (!row) return;

    const names = landingBlocks(form);
    const at = [...list.children].indexOf(row);

    if (target.dataset.drop !== undefined) {
      draw(names.filter((_, index) => index !== at));
      return;
    }

    const way = target.dataset.move;
    if (!way) return;

    const to = way === 'up' ? at - 1 : at + 1;
    if (to < 0 || to >= names.length) return;

    const moved = [...names];
    const [taken] = moved.splice(at, 1);
    moved.splice(to, 0, taken ?? '');
    draw(moved);
  });

  publish?.addEventListener('click', () => {
    setLoading(publish, true);

    void api
      .post(`/api/welcome/landing/publish?guildId=${guildId}`, { guildId })
      .then((result) => {
        const skipped = (result as { skipped?: string[] }).skipped ?? [];

        // The skipped blocks are named: a hole in a landing page is something
        // somebody has to be told about.
        if (skipped.length > 0)
          toastError(`${ct('landingSkipped', 'Missing:')} ${skipped.join(', ')}`);
        else toastSuccess(ct('landingPublished', 'Landing page published.'));
      })
      .catch((error: unknown) => {
        /*
         * The server's own sentence, not a generic one.
         *
         * `publishLanding` refuses for reasons the administrator can act on —
         * no channel chosen, the channel is not a text channel — and each is
         * already translated into sixteen languages. Discarding it left a
         * "could not be published" that named nothing, and four identical 400s
         * in a console with no clue as to what was missing.
         */
        toastError(
          error instanceof Error && error.message
            ? error.message
            : ct('landingFailed', 'The landing page could not be published.'),
        );
      })
      .finally(() => {
        setLoading(publish, false);
      });
  });
}

/**
 * English fallbacks for the warning keys the save returns.
 *
 * `ct` needs one per key so a key forgotten server-side never renders an empty
 * toast; the real sentences live in the sixteen dictionaries.
 */
const WARNING_FALLBACKS: Record<string, string> = {
  ...CARD_BACKGROUND_WARNINGS,
  welcomeChannelMissing: 'The welcome block is on but has no channel: it will post nothing.',
  goodbyeChannelMissing: 'The goodbye block is on but has no channel: it will post nothing.',
  landingChannelMissing: 'The landing page is on but has no channel: it cannot be published.',
};

interface PageData {
  settings: { landing: { blocks: string[] } };
}
